import type { TrackingAttribution } from '~/types/canonicalTracking'

const ATTRIBUTION_STORAGE_KEY = 'grilld_tracking_attribution'
export const TRACKING_ATTRIBUTION_INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000
export const TRACKING_ATTRIBUTION_QUERY_KEYS = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
  fbclid: 'meta_click_id',
  epik: 'pinterest_click_id',
  rdt_cid: 'reddit_click_id',
  sccid: 'snapchat_click_id',
  ttclid: 'tiktok_click_id'
} as const
export const TRACKING_ATTRIBUTION_QUERY_PARAMETERS = Object.keys(TRACKING_ATTRIBUTION_QUERY_KEYS)

export function isTrackingAttributionQueryParameter(key: string): boolean {
  return Object.hasOwn(TRACKING_ATTRIBUTION_QUERY_KEYS, key.toLowerCase())
}

function readStoredAttribution(): TrackingAttribution {
  if (!import.meta.client) return {}

  try {
    const stored = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)
    const attribution = stored ? (JSON.parse(stored) as TrackingAttribution) : {}
    if (isTrackingAttributionExpired(attribution)) {
      sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
      return {}
    }
    return attribution
  } catch {
    return {}
  }
}

function writeStoredAttribution(attribution: TrackingAttribution): void {
  if (!import.meta.client) return

  try {
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution))
  } catch (error) {
    console.warn('Unable to persist tracking attribution', error)
  }
}

function removeStoredAttribution(): void {
  if (!import.meta.client) return

  try {
    sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch (error) {
    console.warn('Unable to clear tracking attribution', error)
  }
}

export function isTrackingAttributionExpired(attribution: TrackingAttribution | undefined, now = Date.now()): boolean {
  if (!attribution || !Object.keys(attribution).length) return false
  const lastActivityAt = attribution.last_activity_at ?? attribution.captured_at
  return !lastActivityAt || now - lastActivityAt >= TRACKING_ATTRIBUTION_INACTIVITY_TIMEOUT_MS
}

export function selectNewestTrackingAttribution(
  first: TrackingAttribution | undefined,
  second: TrackingAttribution | undefined,
  now = Date.now()
): TrackingAttribution | undefined {
  const activeFirst = first && !isTrackingAttributionExpired(first, now) ? first : undefined
  const activeSecond = second && !isTrackingAttributionExpired(second, now) ? second : undefined
  if (!activeFirst) return activeSecond
  if (!activeSecond) return activeFirst
  return (activeSecond.captured_at ?? 0) > (activeFirst.captured_at ?? 0) ? activeSecond : activeFirst
}

function isFreshExternalEntry(url: URL): boolean {
  return (
    url.pathname.startsWith('/order-at-table') ||
    url.pathname.startsWith('/applink') ||
    url.searchParams.has('order-at-table') ||
    url.searchParams.has('applink')
  )
}

export function getAttributionFromUrl(url: URL): TrackingAttribution {
  const captured: TrackingAttribution = {}

  for (const [queryKey, value] of url.searchParams.entries()) {
    const attributionKey =
      TRACKING_ATTRIBUTION_QUERY_KEYS[queryKey.toLowerCase() as keyof typeof TRACKING_ATTRIBUTION_QUERY_KEYS]
    if (attributionKey && value) {
      Object.assign(captured, { [attributionKey]: value })
    }
  }

  const derivedSource =
    (captured.pinterest_click_id && 'pinterest') ||
    (captured.meta_click_id && 'facebook') ||
    (captured.tiktok_click_id && 'tiktok') ||
    (captured.snapchat_click_id && 'snapchat') ||
    (captured.reddit_click_id && 'reddit') ||
    undefined
  if (!captured.source && derivedSource) {
    captured.source = derivedSource
  }

  return captured
}

export function useTrackingAttribution() {
  const attribution = useState<TrackingAttribution>('tracking-attribution', readStoredAttribution)

  function captureFromUrl(url: URL): void {
    const captured = getAttributionFromUrl(url)

    if (!Object.keys(captured).length) {
      if (isFreshExternalEntry(url)) clearAttribution('untagged-external-entry')
      return
    }

    const capturedAt = Date.now()
    // Replace the complete bundle so omitted fields cannot leak in from an
    // earlier campaign.
    attribution.value = {
      ...captured,
      captured_at: capturedAt,
      last_activity_at: capturedAt
    }
    writeStoredAttribution(attribution.value)
  }

  function getActiveAttribution(touchActivity = true, now = Date.now()): TrackingAttribution | undefined {
    if (!Object.keys(attribution.value).length) return undefined
    if (isTrackingAttributionExpired(attribution.value, now)) {
      clearAttribution('expired')
      return undefined
    }

    if (touchActivity) {
      attribution.value = { ...attribution.value, last_activity_at: now }
      writeStoredAttribution(attribution.value)
    }
    return attribution.value
  }

  function clearAttribution(reason = 'cleared'): void {
    if (Object.keys(attribution.value).length) {
      console.info('Tracking attribution cleared', {
        reason,
        source: attribution.value.source,
        ageMs: Date.now() - (attribution.value.captured_at ?? Date.now())
      })
    }
    attribution.value = {}
    removeStoredAttribution()
  }

  return {
    attribution: readonly(attribution),
    captureFromUrl,
    getActiveAttribution,
    clearAttribution
  }
}
